/* ==========================================================================
   LAKSHIV ENTERPRISE — Core behaviour
   Utilities, overlays/drawers, header, navigation, carousels, accordions,
   tabs, reveal animations, toasts, countdowns.

   No dependencies. Every module is defensive: if its markup is absent the
   module simply does nothing.
   ========================================================================== */

(function () {
  'use strict';

  var L = (window.Lakshiv = window.Lakshiv || {});
  L.settings = L.settings || {};
  L.strings = L.strings || {};
  L.routes = L.routes || {};

  /* --- Utilities --------------------------------------------------------- */

  var utils = {
    qs: function (sel, ctx) {
      return (ctx || document).querySelector(sel);
    },

    qsa: function (sel, ctx) {
      return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
    },

    on: function (el, type, handler, opts) {
      if (el) el.addEventListener(type, handler, opts);
    },

    debounce: function (fn, wait) {
      var t;
      return function () {
        var args = arguments;
        var ctx = this;
        clearTimeout(t);
        t = setTimeout(function () {
          fn.apply(ctx, args);
        }, wait || 200);
      };
    },

    throttle: function (fn, limit) {
      var waiting = false;
      var trailing = null;
      return function () {
        var args = arguments;
        var ctx = this;
        if (waiting) {
          trailing = args;
          return;
        }
        fn.apply(ctx, args);
        waiting = true;
        setTimeout(function () {
          waiting = false;
          if (trailing) {
            fn.apply(ctx, trailing);
            trailing = null;
          }
        }, limit || 100);
      };
    },

    /**
     * Formats cents using the shop's money format. Supports the four format
     * tokens Shopify emits; falls back to a plain number if the format is
     * unrecognised so a price is never rendered as "undefined".
     */
    formatMoney: function (cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      var value = '';
      var fmt = format || L.settings.moneyFormat || '${{amount}}';
      var placeholder = /\{\{\s*(\w+)\s*\}\}/;

      function thousands(number, precision, thousandsSep, decimalSep) {
        if (isNaN(number) || number === null) return '0';
        number = (number / 100.0).toFixed(precision);
        var parts = number.split('.');
        var dollars = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          '$1' + (thousandsSep || ',')
        );
        var cents = parts[1] ? (decimalSep || '.') + parts[1] : '';
        return dollars + cents;
      }

      function indian(number, precision) {
        // Shopify's amount_no_decimals_with_comma_separator style still groups
        // in thousands; Indian grouping is applied only for the INR lakh format.
        var num = (number / 100.0).toFixed(precision);
        var parts = num.split('.');
        var last3 = parts[0].slice(-3);
        var rest = parts[0].slice(0, -3);
        if (rest) last3 = ',' + last3;
        rest = rest.replace(/(\d)(?=(\d\d)+$)/g, '$1,');
        return rest + last3 + (parts[1] ? '.' + parts[1] : '');
      }

      var match = fmt.match(placeholder);
      if (!match) return fmt;

      switch (match[1]) {
        case 'amount':
          value = thousands(cents, 2);
          break;
        case 'amount_no_decimals':
          value = thousands(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = thousands(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = thousands(cents, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          value = thousands(cents, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = thousands(cents, 0, ' ', ',');
          break;
        case 'amount_with_period_and_space_separator':
          value = thousands(cents, 2, ' ', '.');
          break;
        default:
          value = thousands(cents, 2);
      }

      // Indian numbering for INR (₹1,00,000 rather than ₹100,000)
      if (fmt.indexOf('₹') !== -1 || fmt.indexOf('INR') !== -1) {
        value = indian(cents, match[1].indexOf('no_decimals') === -1 ? 2 : 0);
      }

      return fmt.replace(placeholder, value);
    },

    /** Replaces `{{ key }}` and `__key__` placeholders in a translation. */
    t: function (str, replacements) {
      if (!str) return '';
      var out = str;
      Object.keys(replacements || {}).forEach(function (key) {
        out = out
          .replace(new RegExp('__' + key + '__', 'g'), replacements[key])
          .replace(
            new RegExp('\\{\\{\\s*' + key + '\\s*\\}\\}', 'g'),
            replacements[key]
          );
      });
      return out;
    },

    prefersReducedMotion: function () {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    /** Fetches a section's rendered HTML and returns a DOM fragment. */
    fetchSection: function (url) {
      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) {
          if (!res.ok) throw new Error('Request failed: ' + res.status);
          return res.text();
        })
        .then(function (text) {
          return new DOMParser().parseFromString(text, 'text/html');
        });
    }
  };

  L.utils = utils;

  /** Guards against double-binding when the theme editor re-renders a section. */
  function once(el) {
    if (!el || el.__lkInit) return false;
    el.__lkInit = true;
    return true;
  }

  /* --- Toasts ------------------------------------------------------------ */

  var Toast = {
    show: function (message, type, duration) {
      var stack = utils.qs('[data-toast-stack]');
      if (!stack || !message) return;

      var toast = document.createElement('div');
      toast.className = 'toast' + (type ? ' toast--' + type : '');
      toast.setAttribute('role', 'status');
      toast.innerHTML =
        '<span aria-hidden="true">' +
        (type === 'error' ? '!' : '✓') +
        '</span><span>' +
        message +
        '</span>';
      stack.appendChild(toast);

      setTimeout(function () {
        toast.classList.add('is-leaving');
        setTimeout(function () {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, 300);
      }, duration || 2600);
    }
  };

  L.toast = Toast;

  /* --- Focus trap -------------------------------------------------------- */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function handler(event) {
      if (event.key === 'Escape') {
        if (onEscape) onEscape();
        return;
      }
      if (event.key !== 'Tab') return;

      var items = utils.qsa(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handler);
    return function release() {
      container.removeEventListener('keydown', handler);
    };
  }

  /* --- Overlay / body lock ----------------------------------------------- */

  var openLayers = [];

  function lockBody() {
    if (openLayers.length === 1) document.body.classList.add('is-locked');
  }

  function unlockBody() {
    if (!openLayers.length) document.body.classList.remove('is-locked');
  }

  function showOverlay() {
    var overlay = utils.qs('[data-overlay]');
    if (!overlay) return;
    overlay.hidden = false;
    // Force a frame so the opacity transition actually runs.
    requestAnimationFrame(function () {
      overlay.classList.add('is-open');
    });
  }

  function hideOverlay() {
    var overlay = utils.qs('[data-overlay]');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    setTimeout(function () {
      if (!openLayers.length) overlay.hidden = true;
    }, 260);
  }

  /**
   * Generic show/hide controller shared by drawers, modals and the mobile
   * search panel. Handles overlay, body lock, focus trapping and restore.
   */
  var Layers = {
    open: function (el, opts) {
      if (!el || openLayers.indexOf(el) !== -1) return;
      opts = opts || {};

      el.hidden = false;
      el._returnFocus = document.activeElement;
      openLayers.push(el);
      lockBody();
      if (opts.overlay !== false) showOverlay();

      requestAnimationFrame(function () {
        el.classList.add('is-open');
        var focusTarget =
          el.querySelector('[data-autofocus]') ||
          el.querySelector(FOCUSABLE);
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      });

      el._releaseTrap = trapFocus(el, function () {
        Layers.close(el);
      });

      el.dispatchEvent(new CustomEvent('layer:open', { bubbles: true }));
    },

    close: function (el) {
      if (!el) return;
      var idx = openLayers.indexOf(el);
      if (idx === -1) return;
      openLayers.splice(idx, 1);

      el.classList.remove('is-open');
      if (el._releaseTrap) el._releaseTrap();

      if (!openLayers.length) hideOverlay();
      unlockBody();

      if (el._returnFocus && typeof el._returnFocus.focus === 'function') {
        el._returnFocus.focus({ preventScroll: true });
      }

      setTimeout(function () {
        if (openLayers.indexOf(el) === -1 && el.hasAttribute('data-hide-when-closed')) {
          el.hidden = true;
        }
      }, 400);

      el.dispatchEvent(new CustomEvent('layer:close', { bubbles: true }));
    },

    closeAll: function () {
      openLayers.slice().forEach(function (el) {
        Layers.close(el);
      });
    },

    isOpen: function (el) {
      return openLayers.indexOf(el) !== -1;
    }
  };

  L.layers = Layers;

  function initLayerTriggers() {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-open]');
      if (opener) {
        var target = document.getElementById(opener.getAttribute('data-open'));
        if (target) {
          event.preventDefault();
          Layers.open(target);
        }
        return;
      }

      var closer = event.target.closest('[data-close], [data-close-modal]');
      if (closer) {
        var layer = closer.closest('.drawer, .modal, .mobile-search, .lightbox');
        if (layer) {
          event.preventDefault();
          Layers.close(layer);
        }
        return;
      }

      if (event.target.matches('[data-overlay]')) {
        Layers.closeAll();
      }
    });

    // Clicking the modal backdrop (outside the dialog) closes it.
    utils.qsa('.modal').forEach(function (modal) {
      utils.on(modal, 'click', function (event) {
        if (event.target === modal) Layers.close(modal);
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && openLayers.length) {
        Layers.close(openLayers[openLayers.length - 1]);
      }
    });
  }

  /* --- Header ------------------------------------------------------------ */

  function initHeader(root) {
    var wrapper = utils.qs('[data-header-wrapper]', root);
    if (!wrapper || !once(wrapper)) return;

    function measure() {
      document.documentElement.style.setProperty(
        '--header-height',
        wrapper.offsetHeight + 'px'
      );
    }

    measure();
    window.addEventListener('resize', utils.debounce(measure, 150));

    var lastY = window.scrollY;
    var hideOnScroll = wrapper.hasAttribute('data-hide-on-scroll');
    var threshold = 120;

    var onScroll = utils.throttle(function () {
      var y = window.scrollY;

      wrapper.classList.toggle('is-stuck', y > 4);

      if (hideOnScroll && !openLayers.length) {
        if (y > lastY && y > threshold) {
          wrapper.classList.add('is-hidden');
        } else {
          wrapper.classList.remove('is-hidden');
        }
      }

      lastY = y;
    }, 100);

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --- Mega menu --------------------------------------------------------- */

  function initMegaMenu(root) {
    var items = utils.qsa('[data-mega-parent]', root).filter(once);
    if (!items.length) return;

    var openTimer;

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-mega-trigger]');
      var panel = item.querySelector('[data-mega-panel]');
      if (!trigger || !panel) return;

      function open() {
        clearTimeout(openTimer);
        closeOthers(item);
        panel.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }

      function close() {
        panel.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }

      item._closeMega = close;

      // Pointer: open on hover with a small close delay to forgive diagonal
      // mouse paths between the trigger and the panel.
      utils.on(item, 'mouseenter', function () {
        if (window.matchMedia('(hover: hover)').matches) open();
      });

      utils.on(item, 'mouseleave', function () {
        if (window.matchMedia('(hover: hover)').matches) {
          openTimer = setTimeout(close, 140);
        }
      });

      // Keyboard / touch: toggle on activation.
      utils.on(trigger, 'click', function (event) {
        if (trigger.tagName === 'A' && window.matchMedia('(hover: hover)').matches) {
          return; // let the link work on desktop
        }
        event.preventDefault();
        if (panel.classList.contains('is-open')) {
          close();
        } else {
          open();
        }
      });

      utils.on(item, 'focusin', open);

      utils.on(item, 'focusout', function (event) {
        if (!item.contains(event.relatedTarget)) close();
      });
    });

    function closeOthers(except) {
      items.forEach(function (item) {
        if (item !== except && item._closeMega) item._closeMega();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeOthers(null);
    });
  }

  /* --- Announcement bar -------------------------------------------------- */

  function initAnnouncement(root) {
    utils.qsa('[data-announcement]', root).filter(once).forEach(function (bar) {
      var items = utils.qsa('.announcement__item', bar);
      if (items.length < 2) return;

      var speed = parseInt(bar.getAttribute('data-speed'), 10) || 5000;
      if (!bar.hasAttribute('data-autoplay') || utils.prefersReducedMotion()) return;

      var index = 0;
      items[0].classList.add('is-active');

      setInterval(function () {
        // Rotation is only visible on mobile (see the CSS); running it always
        // keeps the two layouts in sync when the viewport is resized.
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }, speed);
    });
  }

  /* --- Accordions -------------------------------------------------------- */

  function initAccordions() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      event.preventDefault();

      var panel =
        document.getElementById(trigger.getAttribute('aria-controls')) ||
        trigger.nextElementSibling;
      if (!panel) return;

      var expanded = trigger.getAttribute('aria-expanded') === 'true';
      var group = trigger.closest('[data-accordion-single]');

      if (group && !expanded) {
        utils.qsa('[data-accordion-trigger]', group).forEach(function (other) {
          if (other === trigger) return;
          other.setAttribute('aria-expanded', 'false');
          var otherPanel =
            document.getElementById(other.getAttribute('aria-controls')) ||
            other.nextElementSibling;
          if (otherPanel) otherPanel.classList.remove('is-open');
        });
      }

      trigger.setAttribute('aria-expanded', String(!expanded));
      panel.classList.toggle('is-open', !expanded);

      // Footer columns collapse the whole column, not just the panel.
      var col = trigger.closest('.footer__col--collapsible');
      if (col) col.classList.toggle('is-open', !expanded);
    });
  }

  /* --- Tabs -------------------------------------------------------------- */

  function initTabs(root) {
    utils.qsa('[data-tabs]', root).filter(once).forEach(function (group) {
      var tabs = utils.qsa('[role="tab"]', group);
      if (!tabs.length) return;

      function activate(tab) {
        tabs.forEach(function (item) {
          var selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.setAttribute('tabindex', selected ? '0' : '-1');
          var panel = document.getElementById(item.getAttribute('aria-controls'));
          if (panel) panel.classList.toggle('is-active', selected);
        });
      }

      tabs.forEach(function (tab, i) {
        utils.on(tab, 'click', function (event) {
          event.preventDefault();
          activate(tab);
        });

        utils.on(tab, 'keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (!next) return;
          event.preventDefault();
          next.focus();
          activate(next);
        });
      });
    });
  }

  /* --- Carousels --------------------------------------------------------- */

  function initCarousels(root) {
    utils.qsa('[data-carousel]', root).filter(once).forEach(function (carousel) {
      var track = carousel.querySelector('[data-carousel-track]');
      if (!track) return;

      var prev = carousel.querySelector('[data-carousel-prev]');
      var next = carousel.querySelector('[data-carousel-next]');
      var dotsWrap = carousel.querySelector('[data-carousel-dots]');
      var slides = Array.prototype.slice.call(track.children);
      if (!slides.length) return;

      function pageWidth() {
        // Scroll by one visible "page" so partial cards are never orphaned.
        return track.clientWidth;
      }

      function update() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;

        if (dotsWrap) {
          var pages = Math.max(1, Math.ceil(track.scrollWidth / pageWidth()));
          var current = Math.round(track.scrollLeft / pageWidth());
          utils.qsa('.carousel__dot', dotsWrap).forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === current);
            dot.setAttribute('aria-current', i === current ? 'true' : 'false');
          });
          dotsWrap.hidden = pages < 2;
        }
      }

      function buildDots() {
        if (!dotsWrap) return;
        var pages = Math.max(1, Math.ceil(track.scrollWidth / pageWidth()));
        dotsWrap.innerHTML = '';
        for (var i = 0; i < pages; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          dot.setAttribute(
            'aria-label',
            utils.t(L.strings.goToSlide || 'Go to slide {{ number }}', {
              number: i + 1
            })
          );
          (function (index) {
            dot.addEventListener('click', function () {
              track.scrollTo({
                left: index * pageWidth(),
                behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth'
              });
            });
          })(i);
          dotsWrap.appendChild(dot);
        }
        update();
      }

      function scrollBy(dir) {
        track.scrollBy({
          left: dir * pageWidth(),
          behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth'
        });
      }

      utils.on(prev, 'click', function () {
        scrollBy(-1);
      });
      utils.on(next, 'click', function () {
        scrollBy(1);
      });
      utils.on(track, 'scroll', utils.throttle(update, 100), { passive: true });
      window.addEventListener('resize', utils.debounce(buildDots, 200));

      buildDots();

      // Autoplay — pauses on hover, focus and when off-screen.
      var interval = parseInt(carousel.getAttribute('data-autoplay'), 10);
      if (interval > 0 && !utils.prefersReducedMotion()) {
        var timer = null;
        var visible = true;

        function tick() {
          if (!visible) return;
          var max = track.scrollWidth - track.clientWidth - 2;
          if (track.scrollLeft >= max) {
            track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            scrollBy(1);
          }
        }

        function start() {
          stop();
          timer = setInterval(tick, interval);
        }
        function stop() {
          if (timer) clearInterval(timer);
          timer = null;
        }

        utils.on(carousel, 'mouseenter', stop);
        utils.on(carousel, 'mouseleave', start);
        utils.on(carousel, 'focusin', stop);
        utils.on(carousel, 'focusout', start);

        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            visible = entries[0].isIntersecting;
          }).observe(carousel);
        }

        start();
      }
    });
  }

  /* --- Hero slider ------------------------------------------------------- */

  function initHero(root) {
    utils.qsa('[data-hero]', root).filter(once).forEach(function (hero) {
      var track = hero.querySelector('[data-hero-track]');
      var slides = utils.qsa('[data-hero-slide]', hero);
      if (!track || slides.length === 0) return;

      var dotsWrap = hero.querySelector('[data-hero-dots]');
      var prev = hero.querySelector('[data-hero-prev]');
      var next = hero.querySelector('[data-hero-next]');
      var current = 0;

      function setActive(index) {
        current = index;
        slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === index);
          // Off-screen slides are hidden from assistive tech and tab order.
          slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
          utils.qsa('a, button', slide).forEach(function (el) {
            if (i === index) {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', '-1');
            }
          });
        });
        if (dotsWrap) {
          utils.qsa('.hero__dot', dotsWrap).forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === index);
            dot.setAttribute('aria-current', i === index ? 'true' : 'false');
          });
        }
      }

      function goTo(index) {
        var i = (index + slides.length) % slides.length;
        track.scrollTo({
          left: i * track.clientWidth,
          behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth'
        });
        setActive(i);
      }

      if (dotsWrap && slides.length > 1) {
        slides.forEach(function (_, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'hero__dot';
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          dot.addEventListener('click', function () {
            goTo(i);
            restart();
          });
          dotsWrap.appendChild(dot);
        });
      }

      utils.on(prev, 'click', function () {
        goTo(current - 1);
        restart();
      });
      utils.on(next, 'click', function () {
        goTo(current + 1);
        restart();
      });

      // Keep state in sync when the visitor swipes.
      utils.on(
        track,
        'scroll',
        utils.debounce(function () {
          var index = Math.round(track.scrollLeft / track.clientWidth);
          if (index !== current) setActive(index);
        }, 90),
        { passive: true }
      );

      window.addEventListener(
        'resize',
        utils.debounce(function () {
          track.scrollLeft = current * track.clientWidth;
        }, 200)
      );

      setActive(0);

      var delay = parseInt(hero.getAttribute('data-autoplay'), 10);
      var timer = null;

      function start() {
        if (!delay || delay < 1000 || slides.length < 2) return;
        if (utils.prefersReducedMotion()) return;
        timer = setInterval(function () {
          goTo(current + 1);
        }, delay);
      }

      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      function restart() {
        stop();
        start();
      }

      utils.on(hero, 'mouseenter', stop);
      utils.on(hero, 'mouseleave', start);
      utils.on(hero, 'focusin', stop);
      utils.on(hero, 'focusout', start);

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          stop();
        } else {
          start();
        }
      });

      start();
    });
  }

  /* --- Scroll reveal ----------------------------------------------------- */

  function initReveal(root) {
    if (!L.settings.animations || utils.prefersReducedMotion()) {
      utils.qsa('.reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      utils.qsa('.reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    function observeAll(root) {
      utils.qsa('.reveal:not(.is-visible)', root || document).forEach(function (el) {
        observer.observe(el);
      });
    }

    observeAll();
    L.observeReveal = observeAll;
  }

  /* --- Countdown --------------------------------------------------------- */

  function initCountdowns(root) {
    utils.qsa('[data-countdown]', root).filter(once).forEach(function (el) {
      var target = new Date(el.getAttribute('data-countdown')).getTime();
      if (isNaN(target)) {
        el.classList.add('is-expired');
        return;
      }

      var fields = {
        days: el.querySelector('[data-countdown-days]'),
        hours: el.querySelector('[data-countdown-hours]'),
        minutes: el.querySelector('[data-countdown-minutes]'),
        seconds: el.querySelector('[data-countdown-seconds]')
      };

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      function tick() {
        var diff = target - Date.now();

        if (diff <= 0) {
          el.classList.add('is-expired');
          var onEnd = el.getAttribute('data-countdown-hide-section');
          if (onEnd) {
            var section = el.closest('.shopify-section');
            if (section) section.hidden = true;
          }
          clearInterval(timer);
          return;
        }

        var seconds = Math.floor(diff / 1000);
        if (fields.days) fields.days.textContent = pad(Math.floor(seconds / 86400));
        if (fields.hours) fields.hours.textContent = pad(Math.floor((seconds % 86400) / 3600));
        if (fields.minutes) fields.minutes.textContent = pad(Math.floor((seconds % 3600) / 60));
        if (fields.seconds) fields.seconds.textContent = pad(seconds % 60);
      }

      tick();
      var timer = setInterval(tick, 1000);
    });
  }

  /* --- Back to top ------------------------------------------------------- */

  function initBackToTop() {
    var btn = utils.qs('[data-back-to-top]');
    if (!btn) return;

    window.addEventListener(
      'scroll',
      utils.throttle(function () {
        btn.classList.toggle('is-visible', window.scrollY > 600);
      }, 200),
      { passive: true }
    );

    utils.on(btn, 'click', function () {
      window.scrollTo({
        top: 0,
        behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth'
      });
    });
  }

  /* --- Copy to clipboard (coupon codes) ---------------------------------- */

  function initCopy() {
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-copy]');
      if (!btn) return;

      var value = btn.getAttribute('data-copy');
      var done = function () {
        var original = btn.textContent;
        btn.textContent = L.strings.copied || 'Copied';
        Toast.show(L.strings.copied || 'Copied', 'success', 1500);
        setTimeout(function () {
          btn.textContent = original;
        }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(function () {});
      } else {
        var input = document.createElement('textarea');
        input.value = value;
        input.setAttribute('readonly', '');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        try {
          document.execCommand('copy');
          done();
        } catch (e) {
          /* clipboard unavailable — nothing further to do */
        }
        document.body.removeChild(input);
      }
    });
  }

  /* --- Quantity controls -------------------------------------------------- */

  function initQuantity() {
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-qty-minus], [data-qty-plus]');
      if (!btn) return;

      var wrap = btn.closest('[data-quantity]');
      var input = wrap && wrap.querySelector('.qty__input');
      if (!input) return;

      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10) || Infinity;
      var step = parseInt(input.getAttribute('step'), 10) || 1;
      var value = parseInt(input.value, 10) || min;
      var isPlus = btn.hasAttribute('data-qty-plus');
      var next = isPlus ? value + step : value - step;

      if (next > max) {
        Toast.show(L.strings.maxQuantity, 'error');
        return;
      }
      if (next < min) return;

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      syncQuantityButtons(wrap);
    });

    document.addEventListener('change', function (event) {
      if (!event.target.classList.contains('qty__input')) return;
      var input = event.target;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10) || Infinity;
      var step = parseInt(input.getAttribute('step'), 10) || 1;
      var value = parseInt(input.value, 10);

      if (isNaN(value) || value < min) value = min;
      if (step > 1 && value % step !== 0) value = Math.ceil(value / step) * step;
      if (value > max) {
        value = max;
        Toast.show(L.strings.maxQuantity, 'error');
      }
      input.value = value;
      syncQuantityButtons(input.closest('[data-quantity]'));
    });
  }

  function syncQuantityButtons(wrap) {
    if (!wrap) return;
    var input = wrap.querySelector('.qty__input');
    if (!input) return;
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var max = parseInt(input.getAttribute('max'), 10) || Infinity;
    var value = parseInt(input.value, 10) || min;

    var minus = wrap.querySelector('[data-qty-minus]');
    var plus = wrap.querySelector('[data-qty-plus]');
    if (minus) minus.disabled = value <= min;
    if (plus) plus.disabled = value >= max;
  }

  L.syncQuantityButtons = syncQuantityButtons;

  /* --- Quick view -------------------------------------------------------- */

  function initQuickView() {
    var modal = document.getElementById('QuickViewModal');
    if (!modal) return;

    var content = modal.querySelector('[data-quick-view-content]');

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-quick-view]');
      if (!trigger) return;

      event.preventDefault();
      var url = trigger.getAttribute('data-product-url');
      if (!url) return;

      content.innerHTML =
        '<div class="quick-view__loading"><span class="spinner"></span></div>';
      Layers.open(modal);

      var sep = url.indexOf('?') === -1 ? '?' : '&';
      utils
        .fetchSection(url + sep + 'view=quick')
        .then(function (doc) {
          var markup = doc.querySelector('[data-quick-view-inner]');
          if (!markup) throw new Error('Quick view markup missing');
          content.innerHTML = markup.outerHTML;
          document.dispatchEvent(
            new CustomEvent('lakshiv:section:loaded', { detail: { container: content } })
          );
        })
        .catch(function () {
          content.innerHTML =
            '<div class="empty-state"><p class="empty-state__text">' +
            (L.strings.cartError || 'Something went wrong.') +
            '</p><a class="btn btn--outline" href="' +
            url +
            '">' +
            (L.strings.viewDetails || 'View full details') +
            '</a></div>';
        });
    });
  }

  /* --- Sticky add to cart ------------------------------------------------- */

  function initStickyAtc(root) {
    var bar = utils.qs('[data-sticky-atc]', root);
    var anchor = utils.qs('[data-atc-anchor]', root);
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    if (!once(bar)) return;

    function updateUtilityClearance() {
      var styles = window.getComputedStyle(bar);
      var bottom = Number.parseFloat(styles.bottom) || 0;
      var gap = 12;
      document.documentElement.style.setProperty(
        '--sticky-atc-clearance',
        Math.ceil(bar.offsetHeight + bottom + gap) + 'px'
      );
    }

    new IntersectionObserver(
      function (entries) {
        // Show the bar only once the real button has scrolled past the top.
        var entry = entries[0];
        var below = entry.boundingClientRect.top < 0;
        var isVisible = !entry.isIntersecting && below;
        bar.classList.toggle('is-visible', isVisible);
        document.body.classList.toggle('has-visible-sticky-atc', isVisible);
        if (isVisible) updateUtilityClearance();
      },
      { rootMargin: '-80px 0px 0px 0px' }
    ).observe(anchor);

    window.addEventListener('resize', function () {
      if (bar.classList.contains('is-visible')) updateUtilityClearance();
    });
  }

  /* --- Mobile bottom nav hide-on-scroll ----------------------------------- */

  function initBottomNav() {
    var nav = utils.qs('[data-bottom-nav]');
    if (!nav) return;
    document.body.classList.add('has-bottom-nav');
  }

  /* --- Init -------------------------------------------------------------- */

  function init(root) {
    if (!root) {
      initLayerTriggers();
      initAccordions();
      initQuantity();
      initCopy();
      initQuickView();
      initBackToTop();
      initBottomNav();
    }
    initHeader(root);
    initMegaMenu(root);
    initAnnouncement(root);
    initTabs(root);
    initCarousels(root);
    initHero(root);
    initReveal(root);
    initCountdowns(root);
    initStickyAtc(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: sections are re-rendered without a page reload.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var hero = event.target.querySelector('[data-hero]');
    if (hero) hero.dispatchEvent(new Event('mouseenter'));
  });

  L.init = init;
})();
